import type { Widget, WidgetConfiguration } from "@/lib/api/types";
import {
  ModelMapWidgetConfigurationAllOfBaseLayerStyleEnum as ModelMapBaseLayerStyle,
  ObservationsMapWidgetConfigurationAllOfBaseLayerStyleEnum as ObservationsMapBaseLayerStyle,
} from "@/lib/api/types";

export { ModelMapBaseLayerStyle };
export { ObservationsMapBaseLayerStyle };

/**
 * Mapbox style ids for the org account (`VITE_MAP_USERNAME`). Keys follow each style’s
 * `name` from the Mapbox Styles API (PascalCase, safe for identifiers).
 */
export const knownMapboxStyles = {
  /** Monochrome (light) without labels */
  MonochromeLightWithoutLabels: "cmkfljq3y004601s5fgzu5zrd",
  /** Untitled */
  Untitled: "cmig2sp5r001301s9fsjec7ai",
  /** KNMI */
  Knmi: "cloe42biy001p01pc0wwchmd0",
  /** KNMI - EWT */
  KnmiEwt: "cm9l68jj500vg01s79omohc5i",
  /** KNMI-BES */
  KnmiBes: "clul4lbjw01jr01mj09t591ej",
  /** Monochrome (dark) */
  MonochromeDark: "cmhoyuqfs006c01qu5kg185ch",
  /** Monochrome (light) */
  MonochromeLight: "cmgt6x7hx000301r1fnsg58x7",
} as const;

/**
 * API `base_layer_style` → account style id. Unknown / missing values fall back to
 * {@link knownMapboxStyles.Knmi}.
 */
export const baseLayerMapStyleRegistry = {
  [ModelMapBaseLayerStyle.FullColor]: knownMapboxStyles.Knmi,
  [ModelMapBaseLayerStyle.Monochrome]: knownMapboxStyles.MonochromeLight,
} satisfies Record<ModelMapBaseLayerStyle, string>;

const DEFAULT_MODEL_MAP_ACCOUNT_STYLE_ID = knownMapboxStyles.Knmi;

/**
 * Canonical registry for how dashboard maps resolve a base layer to a Mapbox GL style URL.
 *
 * - **Map widget** stores a Mapbox style id for the account in `VITE_MAP_USERNAME`.
 * - **Model map** and **observations map** widgets use {@link baseLayerMapStyleRegistry}.
 *
 * Account-owned styles listed in CMS: `useMapboxAccountStylesQuery`.
 */

/** Mapbox username from env; account styles in CMS and account-owned map URLs use this owner. */
export function mapboxAccountUsername(): string {
  return import.meta.env.VITE_MAP_USERNAME;
}

/** `mapbox://…` URL for a style id owned by {@link mapboxAccountUsername}. */
export function accountOwnedMapStyleUrl(styleId: string): string {
  return `mapbox://styles/${mapboxAccountUsername()}/${styleId}`;
}

export function mapboxAccountStyleIdForModelMapBaseLayer(baseLayerStyle: string): string {
  return (
    baseLayerMapStyleRegistry[baseLayerStyle as ModelMapBaseLayerStyle] ??
    DEFAULT_MODEL_MAP_ACCOUNT_STYLE_ID
  );
}

export function resolveBaseLayerToMapStyleUrl(baseLayerStyle: string): string {
  return accountOwnedMapStyleUrl(mapboxAccountStyleIdForModelMapBaseLayer(baseLayerStyle));
}

export function isModelMapBaseLayerStyle(v: string): v is ModelMapBaseLayerStyle {
  return v === ModelMapBaseLayerStyle.FullColor || v === ModelMapBaseLayerStyle.Monochrome;
}

function isObservationsMapBaseLayerStyle(v: string): v is ObservationsMapBaseLayerStyle {
  return (
    v === ObservationsMapBaseLayerStyle.FullColor ||
    v === ObservationsMapBaseLayerStyle.Monochrome
  );
}

export function normalizeBaseLayerStyle(values: Widget): Widget {
  const cfg = values.configuration;
  if (!cfg || typeof cfg !== "object" || !("type" in cfg)) {
    return values;
  }
  if (cfg.type === "model_map") {
    const style = (cfg as { base_layer_style?: string }).base_layer_style?.trim();
    if (style && isModelMapBaseLayerStyle(style)) {
      return values;
    }
    return {
      ...values,
      configuration: {
        ...cfg,
        base_layer_style: ModelMapBaseLayerStyle.Monochrome,
      } as WidgetConfiguration,
    };
  }
  if (cfg.type === "observations_map") {
    const style = (cfg as { base_layer_style?: string }).base_layer_style?.trim();
    if (style && isObservationsMapBaseLayerStyle(style)) {
      return values;
    }
    return {
      ...values,
      configuration: {
        ...cfg,
        base_layer_style: ObservationsMapBaseLayerStyle.Monochrome,
      } as WidgetConfiguration,
    };
  }
  return values;
}
